// apps/web/src/features/crm/my-day/components/MyDayBucketSection.tsx
//
// One named bucket (overdue / today / upcoming / blocked) with a header
// carrying the localized label + count, and a vertical list of rows. We
// render an inline empty hint instead of suppressing empty buckets so
// the layout stays stable as items mutate and so the manager always
// sees the same four lanes.
import { useTranslation } from 'react-i18next';
import type { MyDayBucketKind, MyDayItem } from '@magimp/shared';
import { MyDayTaskRow } from './MyDayTaskRow.js';

export interface MyDayBucketSectionProps {
  kind: MyDayBucketKind;
  items: readonly MyDayItem[];
  canManage: boolean;
  /** IANA tz echoed by the backend; rows render `dueAt` against it. */
  timezone: string;
}

const TITLE_KEY: Record<
  MyDayBucketKind,
  'bucket.overdue' | 'bucket.today' | 'bucket.upcoming' | 'bucket.blocked'
> = {
  overdue: 'bucket.overdue',
  today: 'bucket.today',
  upcoming: 'bucket.upcoming',
  blocked: 'bucket.blocked',
};

const EMPTY_KEY: Record<
  MyDayBucketKind,
  'bucketEmpty.overdue' | 'bucketEmpty.today' | 'bucketEmpty.upcoming' | 'bucketEmpty.blocked'
> = {
  overdue: 'bucketEmpty.overdue',
  today: 'bucketEmpty.today',
  upcoming: 'bucketEmpty.upcoming',
  blocked: 'bucketEmpty.blocked',
};

export function MyDayBucketSection({ kind, items, canManage, timezone }: MyDayBucketSectionProps) {
  const { t } = useTranslation('myDay');
  return (
    <section
      data-testid={`my-day-bucket-${kind}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
      }}
    >
      <header style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{t(TITLE_KEY[kind])}</h2>
        <span
          data-testid={`my-day-bucket-${kind}-count`}
          style={{ fontSize: 12, color: 'var(--fg-2)' }}
        >
          {items.length}
        </span>
      </header>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t(EMPTY_KEY[kind])}</div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {items.map((item) => (
            <li key={item.id}>
              <MyDayTaskRow item={item} bucket={kind} canManage={canManage} timezone={timezone} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
